/**
 * Solomon Action Engine — React hook.
 *
 * This is the consumer-facing entry point. Components call
 * `useActionEngine()` to get `{ prepare, execute, isExecuting }`
 * and wire it to a confirm dialog / toast.
 *
 * Why a hook instead of a plain module?
 * -------------------------------------
 * Execution needs auth.user.id, organization_id, and the React
 * Query client (so we can invalidate caches after a mutation).
 * Wrapping those in a hook keeps the call sites short — components
 * pass a proposal and get a typed promise back.
 *
 * The hook does NOT own the confirm UI. It returns `prepare()` so
 * the component (or a shared <ActionProposalCard />) can render the
 * preview however it wants. This separation is what lets the same
 * engine drive the Solomon panel, manual buttons, and the workflow
 * runner's eventual "suggest follow-up actions" surface.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { STAFF_APP_ROLES, useUserRoles } from "@/hooks/useUserRoles";
import { logAudit } from "@/lib/audit";
import { prepareAction as registryPrepareAction } from "./registry";
import type {
  ActionExecutionContext,
  ActionPermissionTier,
  ActionProposal,
  ActionResult,
  ActionSource,
  PreparedAction,
} from "./types";

/**
 * STAFF_APP_ROLES includes platform_readonly — a role that is explicitly
 * view-only everywhere else in the app (isPlatformReadonly gates read-only
 * surfaces). This tier gates real mutations (assign_request, tag_person,
 * add_pastoral_note, ...), not staff-flavored UI visibility, so a read-only
 * support operator shouldn't be promoted to it here.
 */
const MUTATION_STAFF_ROLES = STAFF_APP_ROLES.filter(
  (role) => role !== "platform_readonly",
);

/**
 * Result returned by `execute()`. Mirrors ActionResult plus a
 * `prepared` field so callers can reach the rejection when ok is
 * false because of a preflight check.
 */
export interface ExecuteOutcome<P = Record<string, unknown>> {
  prepared: PreparedAction<P>;
  result: ActionResult | null;
}

export interface UseActionEngineOptions {
  /**
   * Permission tier of the current user. The hook tries to infer
   * this from useOrganization() but consumers can override (e.g. an
   * admin page that wants to test member-tier actions).
   */
  userTier?: ActionPermissionTier;
  /**
   * Default `source` to stamp on proposals that don't specify one.
   * The Solomon panel passes "solomon"; manual buttons pass
   * "manual".
   */
  defaultSource?: ActionSource;
}

const MISSING_SOLOMON_ORG_BINDING =
  "This Solomon proposal is missing its organization binding. Ask Solomon to plan it again.";
const CHANGED_SOLOMON_ORG =
  "This action was planned for a different organization. Switch back to that organization or ask Solomon to plan it again.";

export function useActionEngine(options: UseActionEngineOptions = {}) {
  const { user } = useAuth();
  const { organizationId, organizationRole } = useOrganization();
  const { hasAnyOrgRole, isLoading: rolesLoading } = useUserRoles();
  const [isExecuting, setIsExecuting] = useState(false);
  // Toast actions and other async continuations can outlive the render that
  // created them. Keep tenant checks pointed at the resolved organization id
  // from the latest render, including support-mode tenant targets that are not
  // represented by the fallback `organization` membership object.
  const organizationIdRef = useRef(organizationId);
  organizationIdRef.current = organizationId;

  const userTier: ActionPermissionTier = useMemo(() => {
    if (options.userTier) return options.userTier;
    if (organizationRole === "owner" || organizationRole === "admin") {
      return "org_admin";
    }
    // Granular staff roles (staff, pastor, ministry_leader, etc.) live in
    // user_roles, not organization_members.role. useUserRoles().isStaff
    // rolls up roles across ALL orgs, which would let staff status in one
    // org leak into this one — hasAnyOrgRole scopes to the active org
    // (mirroring public.has_role_in_org), same as isFinanceStaff below.
    //
    // While rolesLoading is true, useAccessContext may still be serving
    // keepPreviousData from the org the user just switched OUT of (see
    // useAccessContext.ts's isDataStale) — hasAnyOrgRole would evaluate
    // against that stale org's roles otherwise. Fail closed to "member"
    // until the active org's roles actually land.
    if (!rolesLoading && hasAnyOrgRole(MUTATION_STAFF_ROLES)) {
      return "staff";
    }
    return "member";
  }, [organizationRole, hasAnyOrgRole, rolesLoading, options.userTier]);

  const proposalOrganizationRejection = useCallback(
    (proposal: ActionProposal<unknown>): string | null => {
      const source =
        proposal.source ?? options.defaultSource ?? "manual";
      const plannedForOrganizationId =
        proposal.plannedForOrganizationId?.trim() || null;

      // A Solomon proposal without a server-owned binding is unsafe. This
      // also fails closed during a rolling deploy if an older edge response
      // reaches a newer SPA.
      if (source === "solomon" && !plannedForOrganizationId) {
        return MISSING_SOLOMON_ORG_BINDING;
      }

      // Enforce any explicit binding regardless of source so inverse/undo
      // proposals retain the original tenant even though undo is stamped as
      // a manual user action.
      if (
        plannedForOrganizationId &&
        plannedForOrganizationId !== organizationIdRef.current
      ) {
        return CHANGED_SOLOMON_ORG;
      }

      return null;
    },
    [options.defaultSource],
  );

  const buildContext = useCallback(
    (proposal: ActionProposal<unknown>): ActionExecutionContext | null => {
      if (!user?.id) return null;
      const currentOrganizationId = organizationIdRef.current;
      if (!currentOrganizationId) return null;
      if (proposalOrganizationRejection(proposal)) return null;
      return {
        userId: user.id,
        organizationId: currentOrganizationId,
        reason: proposal.rationale ?? null,
        source: proposal.source ?? options.defaultSource ?? "manual",
      };
    },
    [
      user?.id,
      options.defaultSource,
      proposalOrganizationRejection,
    ],
  );

  /**
   * Prepare a proposal without executing it. Used by the UI to
   * render a preview + confirm button.
   */
  const prepare = useCallback(
    async <P,>(proposal: ActionProposal<P>): Promise<PreparedAction<P>> => {
      const organizationRejection = proposalOrganizationRejection(proposal);
      if (organizationRejection) {
        return {
          proposal,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          definition: undefined as any,
          preview: { summary: organizationRejection },
          requiresConfirm: true,
          rejection: {
            reason: "precondition_failed",
            message: organizationRejection,
          },
        };
      }
      const ctx = buildContext(proposal);
      if (!ctx) {
        return {
          proposal,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          definition: undefined as any,
          preview: { summary: "Not signed in or no organization selected." },
          requiresConfirm: true,
          rejection: {
            reason: "precondition_failed",
            message: "Not signed in or no organization selected.",
          },
        };
      }
      const prepared = await registryPrepareAction<P>(proposal, ctx, {
        userTier,
      });

      // Preview construction can perform tenant-scoped reads. If the active
      // tenant changed while those reads were in flight, discard the old
      // preview instead of allowing another tenant's names/details to render.
      const postPrepareOrganizationRejection =
        proposalOrganizationRejection(proposal);
      if (postPrepareOrganizationRejection) {
        return {
          proposal,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          definition: undefined as any,
          preview: { summary: postPrepareOrganizationRejection },
          requiresConfirm: true,
          rejection: {
            reason: "precondition_failed",
            message: postPrepareOrganizationRejection,
          },
        };
      }

      return prepared;
    },
    [buildContext, proposalOrganizationRejection, userTier],
  );

  /**
   * Execute a previously-prepared action. Splitting prepare from
   * execute is intentional — the UI gets to render the preview and
   * collect explicit user confirmation between the two calls.
   *
   * On success we surface a toast with an inline Undo button when
   * the action returned an inverse proposal. The Undo simply
   * re-enters the same hook with the inverse proposal — undoing an
   * undo is supported and creates a third audit row, by design.
   */
  const executePrepared = useCallback(
    async <P,>(prepared: PreparedAction<P>): Promise<ActionResult> => {
      if (prepared.rejection) {
        return {
          ok: false,
          summary: prepared.preview.summary,
          error: prepared.rejection.message,
        };
      }
      const organizationRejection = proposalOrganizationRejection(
        prepared.proposal,
      );
      if (organizationRejection) {
        return {
          ok: false,
          summary: organizationRejection,
          error: organizationRejection,
        };
      }
      const ctx = buildContext(prepared.proposal);
      if (!ctx) {
        return {
          ok: false,
          summary: "Not signed in or no organization selected.",
          error: "missing_context",
        };
      }
      setIsExecuting(true);
      try {
        const result = await prepared.definition.execute(
          prepared.proposal.params,
          ctx,
        );

        // Belt-and-suspenders correlation audit. Each executor writes
        // its OWN audit row (the security-relevant one) via logAudit;
        // this engine-level row records the INVOCATION itself so we can
        // correlate "Solomon proposed X → user confirmed → mutation Y
        // landed". Keyed off engine.execute.
        //
        // audit_logs.record_id is `uuid NOT NULL`, so we CANNOT key
        // this row to the action slug (e.g. "assign_request") — the
        // uuid column rejects it and the whole insert fails. Instead we
        // key it to the underlying executor's audit row id (a real
        // uuid), which also makes the correlation row joinable back to
        // the mutation it describes. The slug lives in new_data.action_id,
        // where consumers look for it. When the executor produced no
        // audit row (auditLogId == null — e.g. the action failed before
        // its own audit write), there is no uuid to key to, so we skip
        // the correlation row rather than crash on the NOT NULL column.
        if (result.auditLogId) {
          try {
            await logAudit({
              action: "engine.execute",
              tableName: "solomon_action",
              recordId: result.auditLogId,
              organizationId: ctx.organizationId,
              userId: ctx.userId,
              newData: {
                action_id: prepared.definition.id,
                source: ctx.source,
                reason: ctx.reason,
                ok: result.ok,
                underlying_audit_log_id: result.auditLogId,
                proposal_params: prepared.proposal.params as Record<
                  string,
                  unknown
                >,
              },
            });
          } catch (auditError) {
            // Don't fail the action if engine-level audit fails — the
            // underlying executor's audit row is the security-relevant
            // one. We just lose the correlation.
            console.warn(
              "[solomon-action-engine] engine.execute audit write failed",
              auditError,
            );
          }
        }

        return result;
      } catch (error) {
        return {
          ok: false,
          summary: "Action failed.",
          error: error instanceof Error ? error.message : String(error),
        };
      } finally {
        setIsExecuting(false);
      }
    },
    [buildContext, proposalOrganizationRejection],
  );

  /**
   * Convenience: prepare + execute in one call. Used by callers
   * that have already collected confirmation (e.g. a button that
   * says "Tag as prayer team" — the click IS the confirmation).
   */
  const proposeAndExecute = useCallback(
    async <P,>(proposal: ActionProposal<P>): Promise<ExecuteOutcome<P>> => {
      const prepared = await prepare(proposal);
      if (prepared.rejection) {
        toast.error(prepared.rejection.message);
        return { prepared, result: null };
      }
      const result = await executePrepared(prepared);
      if (result.ok) {
        toast.success(result.summary, {
          action: result.inverse
            ? {
                label: result.inverse.label,
                onClick: () => {
                  void proposeAndExecute({
                    actionId: result.inverse!.actionId,
                    params: result.inverse!.params,
                    rationale: `Undo of ${prepared.definition.id}`,
                    source: "manual",
                    plannedForOrganizationId:
                      prepared.proposal.plannedForOrganizationId,
                  });
                },
              }
            : undefined,
        });
      } else {
        toast.error(result.error ?? result.summary);
      }
      return { prepared, result };
    },
    [prepare, executePrepared],
  );

  return {
    prepare,
    executePrepared,
    proposeAndExecute,
    isExecuting,
    userTier,
  };
}
